import { z } from 'zod';

import { AppealResolutionSchema } from '../appeal-resolution/appeal-resolution';
import { VIOLATION_REVIEW_NOTE_MAX_LENGTH } from '../violation-review/violation-review';

/**
 * The floor on a member's appeal.
 *
 * A **minimum**, unlike a report's optional note, and the asymmetry is
 * deliberate. A report must never put a writing task between a distressed member
 * and getting away from somebody, so its note is optional. An appeal is the
 * opposite situation: the member is asking a human to spend time re-reading a
 * decision, and "no" as a whole submission gives that human nothing to weigh. Low
 * enough that a sentence clears it.
 */
export const APPEAL_TEXT_MIN_LENGTH = 20;

export const APPEAL_TEXT_MAX_LENGTH = 2000;

/**
 * A member contesting an enforcement (member route, no catalog permission — N8).
 *
 * One appeal per enforcement: the state machine has no edge back into
 * `appealed`, so a second submission is refused with
 * `moderation_violation_already_appealed` rather than quietly replacing the
 * first. That is the opposite of a repeat *report*, which upserts — because
 * there the member is trying to get away from somebody and must never meet an
 * error, whereas here the first appeal is already in front of a human and
 * overwriting it would discard what they are reading.
 */
export const ViolationAppealCreateSchema = z
  .object({
    text: z.string().trim().min(APPEAL_TEXT_MIN_LENGTH).max(APPEAL_TEXT_MAX_LENGTH),
  })
  .strict();

export type ViolationAppealCreateRequest = z.infer<typeof ViolationAppealCreateSchema>;

/**
 * A moderator deciding an appeal (`violations.manage`).
 *
 * The resolution is a **field, not the route**, for the reason
 * `ReportResolveSchema` gives: two sibling verdicts on one route beat two
 * endpoints that differ by a string. The asymmetry with Trust's confirm/reject
 * split holds too — that one *bans an account* per branch, whereas both branches
 * here act on a decision that has already been made.
 *
 * An `overturned` verdict reverses the action wherever it holds a state
 * (`isReversibleViolationAction`), which for `ban` means the one reinstatement
 * edge N9 allows — audited, contained, and reachable from nowhere else.
 */
export const ViolationAppealDecideSchema = z
  .object({
    resolution: AppealResolutionSchema,
    note: z
      .string()
      .trim()
      .max(VIOLATION_REVIEW_NOTE_MAX_LENGTH)
      .nullable()
      .default(null)
      .transform((note) => (note === null || note === '' ? null : note)),
  })
  .strict();

export type ViolationAppealDecideRequest = z.infer<typeof ViolationAppealDecideSchema>;
